import PropTypes from 'prop-types'
import React, { useEffect } from 'react'

import config from '@/config'
import lang from '@/lang'
import accountConfig from '@/modules/account/config'
import { toast } from '@/modules/base/components/toast/toastConfig'
import request from '@/modules/base/util/request'
import { PublicAccessContextType } from '@/modules/layout/components/public-access-layout'
import store from '@/redux/store'
import { withAsyncData } from '@/utils/withAsyncData'
import { user } from 'ampersand-app'
import { useOutletContext } from 'react-router'
import Pending from './pending'
import Resend from './resend'
import Success from './success'
import GtmService, { GtmEventTypes } from '@/modules/_services/gtm.service'
import { captureBootstrapError } from '@/modules/account/model/login-bootstrap'

const Approve = (props) => {
  const { state, email } = props

  const outletContext = useOutletContext<PublicAccessContextType>()
  useEffect(() => {
    outletContext.setSidebarUrl(config.registerMarketingPage)
  }, [])

  let content

  switch (state) {
    case 'pending': {
      content = <Pending />
      break
    }
    case 'success': {
      content = <Success />
      break
    }
    case 'resend': {
      content = <Resend email={email} />
      break
    }
  }

  return content
}

Approve.propTypes = {
  email: PropTypes.string,
  state: PropTypes.oneOf(['pending', 'success', 'resend']),
}

export default withAsyncData(Approve, lang.account.documentTitle.approve, async({ params, navigate }) => {
  const { token, email, changeEmailToken } = params
  let state = 'success'
  let overwrittenTitle = null

  if (token) {
    state = 'pending'
    const { tenant: { info: { subscriptions } } } = store.getState()
    const subscriptionTypeTypesLabel = subscriptions?.map(s => s.typeOfSubscription).join(', ') || ''
    const handleSuccess = response => {
      let gtmEvent
      if (ENV.product === 'meinbuero') {
        gtmEvent = { 'event': 'registrierung-bestaetigt' }
      } else {
        gtmEvent = {
          'event': GtmEventTypes.GaEventHub,
          'eventCategory': 'DOI',
          'eventAction': 'E-Mail-Bestaetigung',
          'eventLabel': `DOI durchgeführt ${subscriptionTypeTypesLabel}`,
        }
      }

      GtmService.pushEvent(gtmEvent)
      toast.showToast({ message: lang.accountEmailApproveSuccess })
      const { body: { data } } = response
      // login() owns its own terminal navigation. Returning it unguarded would let a
      // post-login failure run handleFailure and bounce an already logged in user to /login
      return user.login({ data }).catch(error => captureBootstrapError('approve', error))
    }

    const handleFailure = () => {
      toast.showToast({ message: lang.accountEmailApproveError, type: 'error' })
      navigate('/account/login')
    }

    request(accountConfig.endpoints.confirmRegistration, { method: 'PUT', data: { token } })
      .then(handleSuccess)
      .catch(handleFailure)
      .done()
  } else if (changeEmailToken) {
    state = 'pending'

    const handleSuccess = () => {
      toast.showToast({ message: lang.accountEmailChangeSuccessMessage })
    }

    const handleFailure = () => {
      toast.showToast({ message: lang.accountEmailErrorMessage, type: 'error' })
      navigate('/dashboard')
    }

    request(accountConfig.endpoints.confirmChangeEmail, { method: 'PUT', data: { token: changeEmailToken } })
      .then(handleSuccess)
      .catch(handleFailure)
      .done(() => {
        // a logged in user must not be sent to the login page
        if (!user.loggedIn) navigate('/account/login')
      })
  } else if (email) {
    overwrittenTitle = lang.account.documentTitle.approveResend
    state = 'resend'
  }

  return {
    overwrittenTitle,
    state,
    email,
  }
})
